// A pair/team roster as the rest of the app assumes it looks: one player per
// slot, at most `slotSize` of them.
//
// The Участники tab never had to care - it renders slots, not the roster
// (`Array.from({length: slotSize}).map(i => players.find(p => p.sorting === i))`,
// see TournamentPlayers.js/PlayersGridView.js), so an extra member is simply
// invisible there. Every other view maps `team.players` directly and shows
// whatever the backend sent, which is why a corrupted pair surfaces as "3
// участника" in Рейтинг/групах/матчах but not in Участники.
//
// And corrupted pairs do exist in production: `POST /tournaments/{id}/players/`
// has no uniqueness guard on (team, sorting) and no team-size limit, so two
// selections landing on the same slot create two rows instead of replacing
// one. Verified 2026-10-20 - four live doubles tournaments each had exactly one
// team with members at sorting [0, 1, 1]:
//
//   eaf387cc-.../teams/cdcbcb1c-...  Нижник s0 | Гусар s1 | Богданюк s1
//   39d99c8a-.../teams/ca2aed54-...  Артеменко s0 | Святун s1 | Шевченко s1
//   f0dfbcae-.../teams/f0badef0-...  Олійник s0 | Хачатурян s1 | Ткач s1
//   9aee55e2-.../teams/228b44e5-...  Ільницький s0 | Гриців s1 | Гриців s1
//
// See backend-tasks/2026-10-20-pair-with-three-participants.md - the server-
// side guard and the cleanup of the rows above are a backend job. This helper
// only keeps the already-broken rows from leaking into the UI.
//
// Picks the LAST member per slot (backend orders by ("sorting", "created_at"),
// so that's the most recently added one), not the first - all four production
// examples above are a substitution landing on top of the original occupant
// rather than an identical accidental double-submit, so the first-added player
// is the one who's actually gone, and showing them (e.g. in Рейтинг's "Points
// by place" list, or a Groups/Matches card) instead of the real current
// partner read as this helper picking the wrong one, not just a cosmetic "3
// участника" overflow. Last-write-wins (not just "the other" of two) also
// does the right thing if a slot ever picks up a third occupant.
//
// Members with `sorting == null` are deliberately NOT collapsed into one -
// they're not a known-bad shape, just one this helper can't place, and
// dropping them would hide real participants of a team (grade 3) tournament.
export const normalizeTeamPlayers = (players, slotSize) => {
    const list = (players || []).filter(Boolean);
    const bySlot = new Map();
    const unsorted = [];

    for (const p of list) {
        if (p.sorting != null) {
            bySlot.set(p.sorting, p);
        } else {
            unsorted.push(p);
        }
    }

    const kept = [...bySlot.values(), ...unsorted];
    return slotSize > 0 ? kept.slice(0, slotSize) : kept;
};

export default normalizeTeamPlayers;
